import { Component, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, take, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ArkikDialogService } from '@shared/components/arkik-dialog/arkik-dialog.service';
import { ModalConfirmationComponent } from '@shared/components/modal-confirmation/modal-confirmation.component';
import { TranslationService } from '@core/services/common/translation.service';

/**
 * Replaces MsalRedirectComponent: same redirect handling, but catches handleRedirectObservable
 * errors instead of letting them bubble to Angular's global ErrorHandler as an unhandled exception.
 */
@Component({
  selector: 'app-redirect',
  template: '',
})
export class AuthRedirectComponent implements OnInit {
  constructor(
    private authService: MsalService,
    private _dialog: ArkikDialogService,
    private t: TranslationService,
  ) {}

  ngOnInit(): void {
    this.authService
      .initialize()
      .pipe(
        concatMap(() => this.authService.handleRedirectObservable()),
        catchError((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error ?? '');
          this._showErrorAndRedirectToSignIn(message);
          return of(null);
        }),
      )
      .subscribe();
  }

  private _showErrorAndRedirectToSignIn(message: string): void {
    const dialogRef = this._dialog.open(ModalConfirmationComponent, {
      data: {
        title: this.t.pt('views.globals.error'),
        message,
        hideCancelBtn: true,
      },
    });

    dialogRef.afterClosed.pipe(take(1)).subscribe(() => {
      this.authService.instance.setActiveAccount(null);
      this._clearB2CCache();
      window.location.assign(
        `https://login.microsoftonline.com/${environment.B2C_cemexTenantId}/oauth2/v2.0/logout` +
          `?post_logout_redirect_uri=${encodeURIComponent(environment.B2C_redirectUri)}`,
      );
    });
  }

  private _clearB2CCache(): void {
    const isB2CCacheKey = (key: string): boolean =>
      key.startsWith('msal.') || key.includes(environment.B2C_tenantName + '.b2clogin.com');

    [localStorage, sessionStorage].forEach((storage) => {
      Object.keys(storage)
        .filter(isB2CCacheKey)
        .forEach((key) => storage.removeItem(key));
    });
  }
}
